/**
 * Google Integration API Service
 */
import { GoogleAccount, GoogleAuthResponse } from '../types/googleIntegration';

// We'll create a factory function that takes the api instance
// This will be used with the useApi hook in components
export class GoogleIntegrationApiService {
    constructor(private api: any) { }

    /**
     * Get user's connected Google accounts
     */
    async getAccounts(): Promise<GoogleAccount[]> {
        const response = await this.api.get('/api/v1/google/accounts');
        return response.data;
    }

    /**
     * Delete a connected Google account
     */
    async deleteAccount(accountId: number): Promise<GoogleAuthResponse> {
        const response = await this.api.delete(`/api/v1/google/accounts/${accountId}`);
        return response.data;
    }

    /**
     * Get Google OAuth authorization URL
     */
    getAuthUrl(serviceName: string): string {
        // Получаем базовый URL из экземпляра api (axios)
        const baseUrl = this.api.defaults?.baseURL || '';

        const cleanBaseUrl = baseUrl.replace(/\/$/, '');

        const token = localStorage.getItem('access_token');
        const authUrl = `${cleanBaseUrl}/api/v1/google/authorize?service_name=${serviceName}`;

        if (token) {
            return `${authUrl}&token=${token}`;
        }

        return authUrl;
    }
}

// For backward compatibility, we'll also export a function to create the service
export const createGoogleIntegrationApi = (api: any) => new GoogleIntegrationApiService(api);

// Legacy export - this will be deprecated in favor of using the hook
// Keep it for now to avoid breaking existing code, but components should migrate to useApi
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const API_PREFIX = '/api/v1';

class LegacyGoogleIntegrationApiService {
    private getAuthHeaders(): HeadersInit {
        const token = localStorage.getItem('access_token');
        return {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
        };
    }

    private async handleResponse<T>(response: Response): Promise<T> {
        if (!response.ok) {
            // Handle authentication errors manually since we're not using the centralized API
            if (response.status === 401) {
                localStorage.removeItem('access_token');
                localStorage.removeItem('user');
                window.location.href = '/login';
                throw new Error('Authentication failed - redirecting to login');
            }

            const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
            throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
        }
        return response.json();
    }

    async getAccounts(): Promise<GoogleAccount[]> {
        const response = await fetch(`${API_BASE_URL}${API_PREFIX}/google/accounts`, {
            method: 'GET',
            headers: this.getAuthHeaders(),
        });
        return this.handleResponse<GoogleAccount[]>(response);
    }

    async deleteAccount(accountId: number): Promise<GoogleAuthResponse> {
        const response = await fetch(`${API_BASE_URL}${API_PREFIX}/google/accounts/${accountId}`, {
            method: 'DELETE',
            headers: this.getAuthHeaders(),
        });
        return this.handleResponse<GoogleAuthResponse>(response);
    }

    async getAuthUrl(serviceName: string): Promise<string> {
        const response = await fetch(`${API_BASE_URL}${API_PREFIX}/google/authorize?service_name=${serviceName}`, {
            method: 'GET',
            headers: this.getAuthHeaders(),
        });
        return this.handleResponse<string>(response);
    }
}

// Export singleton instance for backward compatibility
export const googleIntegrationApi = new LegacyGoogleIntegrationApiService();
